// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable react/prop-types */
import { isHeading, isListItem } from 'datocms-structured-text-utils';
import React, { useState } from 'react';
// eslint-disable-next-line import/no-unresolved
import { StructuredText, renderNodeRule } from 'react-datocms/structured-text';
// eslint-disable-next-line import/no-extraneous-dependencies
import { format } from 'date-fns';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import TickIcon from '../../../../assets/tick.inline.svg';

import {
	getBreadcrumbsSeoSchema,
	getImageSeoSchema,
	getOrganizationSeoSchema,
} from '../../../../utils/seo-schema';
import { getBreadcrumLocale, pageTypes } from '../../../../utils/url-helpers';
import { Head } from '../../../templates/head';
import PageSection from '../../organisms/page-section';
import { buildLink } from '../../../../utils/locale';
import { EventDate } from '../../../atoms/event-date';
import { ResourceHeader } from '../../../molecules/resource-header';
import { WebinarSignupFormCA } from '../../organisms/webinar-sign-up-form-ca';
import { WebinarSignupForm } from '../../organisms/webinar-sign-up-form';
import LiveSessionCard from '../../molecules/live-session-card';

const NewLiveWebinar = ({ data, location }) => {
	const {
		id,
		dateAndTime,
		mainLogo,
		title,
		speaker,
		timeZone,
		category,
		fullContent,
		timeEnd,
		sessions,
		locale,
	} = data.datoCmsGluLiveWebinar;

	const [formResponse, setFormResponse] = useState(false);
	const date = format(new Date(dateAndTime), 'MMM d, yyyy');
	const time = `${format(new Date(dateAndTime), 'h:mm')} - ${timeEnd}`;

	const resourceHeaderContent = {
		id,
		title,
		speaker,
		categories: [{ name: category?.title }],
	};

	const metaContent = {
		noIndex: data.datoCmsGluLiveWebinar.noIndex,
		noFollow: data.datoCmsGluLiveWebinar.noFollow,
		metaInformation: data.datoCmsGluLiveWebinar.metaTags,
		fallbackTitle: data.datoCmsGluLiveWebinar.eLearningTitle,
		fallbackDescription: '',
		canonicalUrl:
			data.datoCmsGluLiveWebinar?.canonicalUrl || location?.href,
	};

	const seoSchemas = {
		organization: getOrganizationSeoSchema({
			data,
			url: `${location?.origin}/${getBreadcrumLocale(location)}`,
		}),
		breadcrumbs: getBreadcrumbsSeoSchema({
			data,
			pageType: pageTypes.gluLiveWebinar,
			location,
		}),
		image: getImageSeoSchema(mainLogo?.url),
	};

	return (
		<GoogleReCaptchaProvider
			reCaptchaKey={process.env.GATSBY_RECAPTCHA_KEY}
		>
			<Head metaContent={metaContent} seoSchemas={seoSchemas} />

			<div className="">
				<PageSection>
					<p className="-mt-7 glulg:w-[50%] gluxs:w-full">
						<a
							href={buildLink(locale, '/elearning/webinars')}
							className="underline "
						>
							Webinars
						</a>
						<span className="mx-4">/</span>
						{title}
					</p>
					<div className="flex flex-col lg:flex-row">
						<div
							className={`w-full lg:w-1/2 ${
								!resourceHeaderContent.author ? 'mb-20' : null
							}`}
						>
							<EventDate date={date} time={time} />
							<ResourceHeader
								className="!px-0"
								content={resourceHeaderContent}
								variant="Event"
							/>
							<br />
							<div className="w-full min-h-[300px]">
								<h2 className="mb-5 text-3xl font-bold">
									About this upcoming session
								</h2>
								<StructuredText
									data={fullContent.value}
									customNodeRules={[
										renderNodeRule(
											isHeading,
											({ node, children, key }) => {
												const HeadingTag = `h${node.level}`;
												return (
													<HeadingTag
														className="mb-5 text-2xl font-bold lg:text-3xl font-lexend-regular"
														key={key}
													>
														{children}
													</HeadingTag>
												);
											}
										),
										renderNodeRule(
											isListItem,
											({ children, key }) => (
												<li
													className="flex items-center mt-5 mb-4 text-sm last-of-type:mb-0 font-lexend-light"
													key={key}
												>
													<TickIcon
														className="w-3 h-auto mr-4 text-blue-400 shrink-0"
														aria-hidden="true"
													/>

													{children}
												</li>
											)
										),
									]}
								/>
							</div>
						</div>

						<div className="relative w-full px-2 py-5 lg:w-1/2 lg:p-10">
							{!formResponse ? (
								<div className="top-0 lg:absolute">
									{locale === 'en-CA' ? (
										<div className="-mt-16">
											<WebinarSignupFormCA
												dateAndTime={dateAndTime}
												pageUrl={
													typeof window !==
													'undefined'
														? window.location.href
														: ''
												}
												setFormResponse={
													setFormResponse
												}
												formId={4777}
												timeZone={timeZone}
											/>
										</div>
									) : (
										<WebinarSignupForm
											dateAndTime={dateAndTime}
											pageUrl={
												typeof window !== 'undefined'
													? window.location.href
													: ''
											}
											setFormResponse={setFormResponse}
											formId={
												locale === 'en-IE' ? 6736 : 4925
											}
											locale={locale}
											pageType="elearning"
										/>
									)}
								</div>
							) : (
								<div className="flex flex-col overflow-hidden rounded-sm">
									<h3 className="w-full p-4 text-2xl text-white bg-brand-blue-400 lg:p-8">
										<TickIcon
											className="inline-block w-5 h-auto mr-4 text-white shrink-0"
											aria-hidden="true"
										/>{' '}
										Your spot has been secured
									</h3>
									<div className="p-4 bg-white lg:p-8">
										<p className="mb-5 font-bold">
											Thank you for registering your spot
											to our webinar{' '}
											<span className="text-brand-red-400">
												{title}
											</span>
										</p>
										<p>
											A reminder of the start date has
											been sent out to your registered
											email with the details of the
											webinar.
										</p>
									</div>
								</div>
							)}
						</div>
					</div>
				</PageSection>
			</div>

			{sessions?.length > 0 && (
				<PageSection logo={mainLogo} titleBlock="Similar live sessions">
					<div className="grid h-full grid-cols-1 gap-10 lg:grid-cols-3">
						{sessions.map((session) => (
							<div key={session.title}>
								<LiveSessionCard
									session={session}
									locale={locale}
								/>
							</div>
						))}
					</div>
				</PageSection>
			)}
		</GoogleReCaptchaProvider>
	);
};

export default NewLiveWebinar;
